import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { bookDeal, getVenueOffers, PublicOffer, recordOfferView, RestoDto, venueImageUrl } from '../api';
import { publicValidity } from './partner/shared';
import { useVenue } from '../hooks/useVenues';
import { mockOffers } from '../lib/mock';
import { PROPOSE_UI_COLORS } from '../theme';
import Logo from './Logo';
import { trackEvent } from '../analytics';

const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?auto=format&fit=crop&w=800&q=60';

function randomClaimCode(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let code = 'C2N';
  for (let i = 0; i < 5; i++) code += chars[Math.floor(Math.random() * chars.length)];
  return code;
}

// Ported from c2n-php's bookDeal flow (FrontController.php / MessageService::sendMessage) for
// real venues (kind=resto — the only kind Deal.RestoId can reference), restyled to match the
// reference design's Claim Offer screen exactly: a single bordered card (logo, "Claim Offer"
// header, image, details, QR, code, one Download/Share button). The real promo code is SMS'd to
// the customer's mobile by the backend, same as legacy; the QR/claim code shown here is a
// display-only reference code, not the redemption code itself. Mock/wine-shop offers (no real
// Deal to create) get a purely illustrative local claim code — there's no booking backend for them.
const ClaimOfferPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const [kind, idStr] = (slug ?? '').split('-');
  const id = Number(idStr);
  // Looked up by id, so claim links work wherever the venue is (not just near the visitor).
  const { venue } = useVenue(kind, id);
  // ?offer=<id> picks one of the venue's partner-portal offers; otherwise its headline offer.
  const requestedOfferId = Number(searchParams.get('offer')) || undefined;
  // The partner portal's "View" button adds preview=1 so partners don't inflate their own view counts.
  const isPreview = searchParams.get('preview') === '1';

  const [liveOffers, setLiveOffers] = useState<PublicOffer[]>([]);
  useEffect(() => {
    if (kind !== 'resto' || !id) return;
    getVenueOffers(id).then(setLiveOffers).catch(() => setLiveOffers([]));
  }, [kind, id]);

  const offerInfo = useMemo(() => {
    if (kind === 'resto') {
      const r = venue as RestoDto | null;
      if (r) {
        const offerId = requestedOfferId ?? r.offerId;
        const live = liveOffers.find((o) => o.id === offerId);
        if (live) {
          return {
            title: live.benefit ? `${live.title} · ${live.benefit}` : live.title, venue: r.name, location: r.address,
            image: venueImageUrl(live.coverImageUrl) ?? venueImageUrl(r.filePath) ?? FALLBACK_IMAGE,
            validity: publicValidity(live), terms: live.terms, minimumSpend: live.minimumSpend, offerId: live.id, bookable: true
          };
        }
        return { title: r.offer || 'Special Offer', venue: r.name, location: r.address, image: venueImageUrl(r.offerImageUrl) ?? venueImageUrl(r.filePath) ?? FALLBACK_IMAGE, validity: 'Valid Tonight · Until 11:30 PM', terms: undefined, minimumSpend: undefined, offerId: requestedOfferId, bookable: true };
      }
    }
    const mock = mockOffers.find((o) => o.id === id);
    if (mock) return { title: mock.title, venue: mock.venue, location: mock.location, image: mock.image, validity: 'Valid Tonight · Until 11:30 PM', terms: undefined, minimumSpend: undefined, offerId: undefined, bookable: false };
    return { title: 'Special Offer', venue: 'Cheers2Night Partner', location: 'Pune', image: FALLBACK_IMAGE, validity: 'Valid Tonight · Until 11:30 PM', terms: undefined, minimumSpend: undefined, offerId: undefined, bookable: false };
  }, [kind, id, venue, liveOffers, requestedOfferId]);

  const viewedOfferId = offerInfo.bookable && !isPreview && liveOffers.some((o) => o.id === offerInfo.offerId) ? offerInfo.offerId : undefined;
  useEffect(() => {
    if (viewedOfferId) recordOfferView(viewedOfferId);
  }, [viewedOfferId]);

  const [mobile, setMobile] = useState('');
  const [claim, setClaim] = useState<{ code: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleClaim = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      if (offerInfo.bookable) {
        const result = await bookDeal(id, mobile, offerInfo.offerId);
        if (!result.success) {
          setError(result.message);
          return;
        }
      }
      trackEvent('claim_offer', { venue: offerInfo.venue, offer: offerInfo.title, offer_id: offerInfo.offerId, venue_id: id || undefined, demo: !offerInfo.bookable });
      setClaim({ code: randomClaimCode() });
    } catch (err) {
      // Claim limits (fully claimed, already claimed) come back as 400s with a message for the customer.
      setError((err as { response?: { data?: { message?: string } } })?.response?.data?.message
        ?? 'Could not claim this offer right now. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 420, mx: 'auto', px: 3, pt: 12, pb: 8 }}>
      <Box mb={2}><Logo /></Box>

      <Box
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          background: PROPOSE_UI_COLORS.surface,
          border: '1px solid rgba(245,246,248,0.08)'
        }}
      >
        <Box textAlign="center" py={2} borderBottom="1px solid rgba(245,246,248,0.08)">
          <Typography fontWeight={700} fontSize={18}>Claim Offer</Typography>
        </Box>

        <img src={offerInfo.image} alt={offerInfo.title} style={{ width: '100%', height: 160, objectFit: 'cover' }} />

        <Box p={3} textAlign="center">
          <Typography fontWeight={700} fontSize={20} mb={0.5}>{offerInfo.title}</Typography>
          <Typography color="text.secondary" mb={1}>{offerInfo.venue}, {offerInfo.location}</Typography>
          <Box display="flex" alignItems="center" justifyContent="center" gap={0.5} color="success.main" fontSize={13} mb={claim ? 3 : 0}>
            <AccessTimeIcon sx={{ fontSize: 15 }} />
            <Typography fontSize={13} color="text.secondary">{offerInfo.validity}</Typography>
          </Box>
          {offerInfo.minimumSpend && <Typography fontSize={13} mt={1}>Minimum spend: ₹{offerInfo.minimumSpend.toLocaleString('en-IN')}</Typography>}
          {offerInfo.terms && <Typography fontSize={12} color="text.secondary" mt={1} sx={{ whiteSpace: 'pre-line' }}>T&C: {offerInfo.terms}</Typography>}

          {!claim && (
            <Box component="form" onSubmit={handleClaim} display="flex" flexDirection="column" gap={2} mt={3} textAlign="left">
              {error && <Alert severity="error">{error}</Alert>}
              {!offerInfo.bookable && (
                <Alert severity="info">This is a preview offer — claiming it generates a demo pass, not a real redeemable code.</Alert>
              )}
              <TextField label="Your Mobile Number" required value={mobile} onChange={(e) => setMobile(e.target.value)} />
              <Button type="submit" variant="contained" color="primary" size="large" disabled={loading}>
                {loading ? 'Claiming...' : 'Claim Offer'}
              </Button>
            </Box>
          )}

          {claim && (
            <>
              <Typography color="text.secondary" fontSize={14} mb={2}>
                Show this QR code at the venue{offerInfo.bookable ? ' — your promo code has also been sent by SMS.' : '.'}
              </Typography>
              <Box display="flex" justifyContent="center" p={2} bgcolor="#fff" borderRadius={2} width="fit-content" mx="auto" mb={2}>
                <QRCodeSVG value={claim.code} size={160} />
              </Box>
              <Typography fontWeight={800} fontSize={20} letterSpacing={1} mb={3}>{claim.code}</Typography>
              <Button variant="contained" color="primary" size="large" fullWidth>Download / Share</Button>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ClaimOfferPage;
